import React, { useEffect, useMemo, useState } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import logo from "../assets/siigmatic-logo.png";
import axiosInstance from "../utils/axiosInstance";
import { clearCurrentUserCache, getCurrentUser } from "../utils/authClient";
import { SKIP_AUTH, getDevAuthUser } from "../utils/authMode";
import {
    LayoutDashboard,
    Gauge,
    Shield,
    Circle,
    Menu,
    X,
    LogOut,
    Building2,
    MessageCircle,
    Workflow,
    Sigma,
    Sparkles,
    BriefcaseBusiness,
    ChevronDown,
    ExternalLink,
    Settings,
} from "lucide-react";

const Layout = () => {
    const location = useLocation();
    const navigate = useNavigate();

    const [open, setOpen] = useState(false);
    const [signingOut, setSigningOut] = useState(false);

    const [auth, setAuth] = useState(() => ({
        username: SKIP_AUTH ? getDevAuthUser().username : localStorage.getItem("username"),
        email: SKIP_AUTH ? getDevAuthUser().email : localStorage.getItem("email"),
        role: SKIP_AUTH ? getDevAuthUser().role : localStorage.getItem("role") || "user",
    }));

    const menuItems = useMemo(() => {
        const items = [
            { path: "/dashboard", name: "Dashboard" },
            { path: "/lineardecomposer", name: "Window Energy Rating" },
            // { path: "/settings", name: "Settings" },
        ];

        if (auth.role === "admin") {
            items.push({ path: "/admin", name: "Admin" });
        }

        return items;
    }, [auth.role]);

    const iconMap = useMemo(
        () => ({
            "/dashboard": LayoutDashboard,
            "/lineardecomposer": Gauge,
            "/settings": Settings,
            "/admin": Shield,
        }),
        []
    );

    const syncAuthFromStorage = () => {
        setAuth({
            username: localStorage.getItem("username"),
            email: localStorage.getItem("email"),
            role: localStorage.getItem("role") || "user",
        });
    };

    const loadAuthFromServer = async () => {
        if (SKIP_AUTH) {
            const devUser = getDevAuthUser();
            localStorage.setItem("email", devUser.email);
            localStorage.setItem("username", devUser.username);
            localStorage.setItem("role", devUser.role);
            setAuth({
                username: devUser.username,
                email: devUser.email,
                role: devUser.role,
            });
            return;
        }

        try {
            const data = await getCurrentUser();

            const email = data?.email || "";
            const username =
                data?.username ||
                localStorage.getItem("username") ||
                (email ? email.split("@")[0] : "");
            const role = data?.role || "user";

            localStorage.setItem("email", email);
            localStorage.setItem("username", username);
            localStorage.setItem("role", role);

            setAuth({
                username,
                email,
                role,
            });
        } catch (_err) {
            localStorage.removeItem("username");
            localStorage.removeItem("email");
            localStorage.removeItem("role");

            setAuth({
                username: null,
                email: null,
                role: "user",
            });
        }
    };

    useEffect(() => {
        loadAuthFromServer();

        window.addEventListener("authUpdated", syncAuthFromStorage);
        window.addEventListener("storage", syncAuthFromStorage);

        return () => {
            window.removeEventListener("authUpdated", syncAuthFromStorage);
            window.removeEventListener("storage", syncAuthFromStorage);
        };
    }, []);

    useEffect(() => {
        setOpen(false);
    }, [location.pathname]);

    const signOut = async () => {
        try {
            setSigningOut(true);
            await axiosInstance.post("/user/logout");
        } catch (_err) {
            // Clear local state even if backend logout fails
        } finally {
            localStorage.removeItem("username");
            localStorage.removeItem("email");
            localStorage.removeItem("role");
            clearCurrentUserCache();
            window.dispatchEvent(new Event("authUpdated"));
            setSigningOut(false);
            navigate("/signin", { replace: true });
        }
    };

    const DesktopNavLinks = () => (
        <nav className="hidden md:flex items-center">
            <ul className="flex items-center gap-2">
                {menuItems.map(({ path, name }, i) => {
                    const active = location.pathname === path;
                    const Icon = iconMap[path] || Circle;

                    return (
                        <li key={`${path}-${i}`}>
                            <Link
                                to={path}
                                className={[
                                    "flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-colors duration-150 border",
                                    active
                                        ? "bg-slate-900 text-white border-slate-900 shadow-sm"
                                        : "bg-white text-slate-700 border-slate-300 hover:bg-slate-100 hover:text-slate-900",
                                ].join(" ")}
                            >
                                <Icon
                                    className={`h-4 w-4 shrink-0 ${active ? "text-white" : "text-slate-400"
                                        }`}
                                    aria-hidden="true"
                                />
                                <span className="truncate">{name}</span>
                            </Link>
                        </li>
                    );
                })}
            </ul>
        </nav>
    );

    const DrawerNavLinks = ({ onNavigate }) => (
        <nav className="w-full">
            <ul className="flex flex-col gap-2 px-3 md:px-4 py-3">
                {menuItems.map(({ path, name }, i) => {
                    const active = location.pathname === path;
                    const Icon = iconMap[path] || Circle;

                    return (
                        <li key={`${path}-${i}`} className="w-full">
                            <Link
                                to={path}
                                onClick={onNavigate}
                                className={[
                                    "flex w-full items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-colors duration-150 border",
                                    active
                                        ? "bg-slate-900 text-white border-slate-900 shadow-sm"
                                        : "bg-white text-slate-700 border-slate-300 hover:bg-slate-100 hover:text-slate-900",
                                ].join(" ")}
                            >
                                <Icon
                                    className={`h-4 w-4 shrink-0 ${active ? "text-white" : "text-slate-400"
                                        }`}
                                    aria-hidden="true"
                                />
                                <span className="truncate">{name}</span>
                            </Link>
                        </li>
                    );
                })}
            </ul>
        </nav>
    );

    const quickLinks = [
        { label: "Intranet", url: "https://www.ciilock.com/catalogues/intranet/0212intra0au07", icon: Building2 },
        { label: "Chat", url: "https://chat.ciilock.com", icon: MessageCircle },
        { label: "Kaizen", url: "https://kaizen.ciilock.com", icon: Workflow },
        { label: "Euler", url: "https://euler.ciilock.com", icon: Sigma },
        { label: "Enhance", url: "https://enhance.ciilock.com", icon: Sparkles },
        { label: "Talent", url: "https://talent.ciilock.com", icon: BriefcaseBusiness },
    ];

    const QuickLinksMenu = ({ className = "" }) => {
        const [quickMenuOpen, setQuickMenuOpen] = useState(false);

        useEffect(() => {
            if (!quickMenuOpen) return undefined;
            const handleOutsideClick = (event) => {
                const root = event.target.closest("[data-quick-links-root]");
                if (!root) setQuickMenuOpen(false);
            };
            window.addEventListener("mousedown", handleOutsideClick);
            return () => window.removeEventListener("mousedown", handleOutsideClick);
        }, [quickMenuOpen]);

        const openLink = (url) => {
            window.open(url, "_blank", "noopener,noreferrer");
            setQuickMenuOpen(false);
        };

        return (
            <div data-quick-links-root className={`relative ${className}`}>
                <button
                    type="button"
                    onClick={() => setQuickMenuOpen((prev) => !prev)}
                    className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-3 py-2 shadow-sm transition-colors hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-400/60"
                >
                    <div className="flex h-10 w-44 items-center justify-center rounded-xl border border-slate-200 bg-white">
                        <img
                            src={logo}
                            alt="SiiGMATIC logo"
                            className="h-10 w-36 object-contain"
                        />
                    </div>
                    <ChevronDown
                        className={`h-4 w-4 text-slate-500 transition-transform ${quickMenuOpen ? "rotate-180" : ""}`}
                    />
                </button>

                {quickMenuOpen && (
                    <div className="absolute left-0 top-[calc(100%+8px)] z-50 w-72 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl">
                        <div className="border-b border-slate-100 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                            Click to navigate
                        </div>
                        <ul className="p-2">
                            {quickLinks.map((link) => {
                                const Icon = link.icon;
                                return (
                                    <li key={link.label}>
                                        <button
                                            type="button"
                                            onClick={() => openLink(link.url)}
                                            className="flex w-full items-center gap-3 rounded-xl px-3 py-2 text-left transition-colors hover:bg-slate-100"
                                        >
                                            <Icon className="h-4 w-4 text-slate-500" />
                                            <span className="flex-1 text-sm font-semibold text-slate-800">
                                                {link.label}
                                            </span>
                                            <ExternalLink className="h-3.5 w-3.5 text-slate-400" />
                                        </button>
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="min-h-screen w-full bg-gray-100 flex flex-col">
            <header className="sticky top-0 z-40 w-full border-b border-slate-200 bg-gray-100">
                <div className="h-16 flex items-center justify-between px-4 gap-4">
                    <div className="flex items-center gap-3 shrink-0">
                        {auth.role === "admin" ? (
                            <QuickLinksMenu />
                        ) : (
                            <div className="flex h-10 w-44 items-center justify-center rounded-2xl border border-slate-200 bg-white shadow-sm">
                                <img
                                    src={logo}
                                    alt="SiiGMATIC logo"
                                    className="h-10 w-36 object-contain"
                                />
                            </div>
                        )}
                    </div>

                    <div className="hidden md:flex flex-1 min-w-0 justify-center">
                        <div className="max-w-full overflow-x-auto no-scrollbar">
                            <DesktopNavLinks />
                        </div>
                    </div>

                    <div className="flex items-center gap-3 shrink-0">
                        <span className="hidden sm:inline text-sm font-medium text-slate-700">
                            {auth.username || auth.email || "User"}
                        </span>

                        <button
                            onClick={signOut}
                            type="button"
                            disabled={signingOut}
                            className="hidden sm:inline-flex items-center gap-2 px-4 py-2 rounded-full bg-red-600 text-white shadow-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-opacity-75 transition duration-200 ease-in-out disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                            <LogOut className="h-4 w-4" />
                            <span>{signingOut ? "Signing out..." : "Sign out"}</span>
                        </button>

                        <button
                            aria-label="Open navigation"
                            aria-expanded={open}
                            aria-controls="mobile-drawer"
                            onClick={() => setOpen(true)}
                            className="md:hidden p-2 rounded focus:outline-none focus:ring-2 focus:ring-slate-400/60"
                        >
                            <Menu className="h-7 w-7 text-slate-800" />
                        </button>
                    </div>
                </div>
            </header>

            <div
                id="mobile-drawer"
                className={`fixed inset-0 z-50 md:hidden ${open ? "" : "pointer-events-none"}`}
            >
                <div
                    className={`absolute inset-0 bg-black/40 transition-opacity ${open ? "opacity-100" : "opacity-0"
                        }`}
                    onClick={() => setOpen(false)}
                    aria-hidden="true"
                />

                <div
                    className={`absolute left-0 top-0 h-full w-[18rem] bg-gray-100 shadow-xl transition-transform duration-300 ${open ? "translate-x-0" : "-translate-x-full"
                        }`}
                    role="dialog"
                    aria-modal="true"
                >
                    <div className="relative h-full flex flex-col">
                        <button
                            aria-label="Close navigation"
                            onClick={() => setOpen(false)}
                            className="absolute right-3 top-3 p-2 rounded focus:outline-none focus:ring-2 focus:ring-slate-400/60"
                        >
                            <X className="h-6 w-6 text-slate-700" />
                        </button>

                        <div className="flex items-center gap-3 shrink-0 mt-4 ml-4 mr-4">
                            {auth.role === "admin" ? (
                                <QuickLinksMenu />
                            ) : (
                                <div className="flex h-10 w-44 items-center justify-center rounded-2xl border border-slate-200 bg-white shadow-sm">
                                    <img
                                        src={logo}
                                        alt="SiiGMATIC logo"
                                        className="h-10 w-36 object-contain"
                                    />
                                </div>
                            )}
                        </div>

                        <div className="flex-1 overflow-y-auto">
                            <DrawerNavLinks onNavigate={() => setOpen(false)} />
                        </div>

                        <div className="p-4 border-t border-slate-200">
                            <div className="flex flex-col items-center text-center">
                                <div className="mb-1 font-semibold text-md">
                                    {auth.username || "User"}
                                </div>
                                <div className="mb-1 text-sm text-slate-600 break-all">
                                    {auth.email || "Email"}
                                </div>
                                <div className="mb-3 text-xs uppercase tracking-wide text-slate-500">
                                    {auth.role || "user"}
                                </div>
                                <button
                                    onClick={signOut}
                                    type="button"
                                    disabled={signingOut}
                                    className="inline-flex w-full items-center justify-center gap-2 px-4 py-2 rounded-full bg-red-600 text-white shadow-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-opacity-75 transition duration-200 ease-in-out disabled:opacity-60 disabled:cursor-not-allowed"
                                >
                                    <LogOut className="h-4 w-4" />
                                    <span>{signingOut ? "Signing out..." : "Sign out"}</span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <main className="flex-1 w-full max-w-full overflow-y-auto">
                <Outlet />
            </main>

            <footer className="w-full border-t border-slate-200 bg-gray-100">
                <div className="mx-auto w-full max-w-full px-4 py-3 text-xs text-slate-500 flex items-center justify-between">
                    <span>{auth.email || ""}</span>
                    <span className="hidden sm:inline">© SiiGMATIC</span>
                </div>
            </footer>
        </div>
    );
};

export default Layout;
